import parse from 'html-react-parser';
import {
  Button,
  Chip,
  CircularProgress,
  Divider,
  Stack,
  Typography,
} from '@mui/material';
import React, { FC, useState } from 'react';
import { Position as BasePosition } from '@staffedupusa/shared-components';
import { jobPostingStatuses } from '@features/jobPostings';
import { useUpdateTemplateJobPosting } from '../hooks/useUpdateTemplateJobPosting';
import TemplateJobPostingModal from './TemplateJobPostingModal';
import ArchivePositionModal from '@features/jobPostings/components/ArchivePositionModal/ArchivePositionModal';
import { EnterpriseTemplate } from '../types';
import AboutJobPostingModal from '@features/jobPostings/components/AboutJobPostingModal/AboutJobPostingModal';
import { formSalaryString } from '@features/jobPostings/components/utils';
import { getCookie } from '@utils/getCookie';
import ConfirmationModal from './ConfirmationModal';
import { Box } from '@mui/system';
type Position = BasePosition & {
  bonus?: boolean;
  incentives?: boolean;
  benefits?: boolean;
  is_archive?: boolean;
};
type TemplatePositionCardProps = {
  jobPosting: Position;
  templateData: EnterpriseTemplate;
};

const TemplatePositionCard: FC<TemplatePositionCardProps> = ({
  jobPosting,
  templateData,
}) => {
  const dontShowWarningPopup = getCookie('dontShowWarningPopup');
  const isAppliedChanges = templateData.apply_changes && !dontShowWarningPopup;
  const [selectedJobPosting, setSelectedJobPosting] = useState<Position | null>(
    null,
  );
  const [isAboutJobPostingModalOpen, setIsAboutJobPostingModalOpen] =
    useState(false);
  const [errorText, setErrorText] = useState<string>('');
  const [isJobPostingModalOpen, setIsJobPostingModalOpen] = useState(false);
  const [archiveJobId, setArchiveModalId] = useState<number | null>(null);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [pendingUpdate, setPendingUpdate] = useState<Position | null>(null);
  const [pendingArchiveId, setPendingArchiveId] = useState<number | null>(null);
  const [pendingStatusUpdate, setPendingStatusUpdate] = useState<{
    id: number;
    status: number;
    template_id: number;
  } | null>(null);
  const { updateJobPosting, isUpdatingJobPosting } =
    useUpdateTemplateJobPosting({ setErrorText });

  const handleConfirm = () => {
    if (pendingUpdate) {
      setSelectedJobPosting(pendingUpdate);
      setIsJobPostingModalOpen(true);
    } else if (pendingStatusUpdate?.id) {
      updateJobPosting({
        id: pendingStatusUpdate.id as number,
        status: pendingStatusUpdate.status,
        template_id: pendingStatusUpdate.template_id,
      });
    } else if (pendingArchiveId) {
      setArchiveModalId(Number(pendingArchiveId));
    }
    setPendingStatusUpdate(null);
    setPendingUpdate(null);
    setPendingArchiveId(null);
    setIsConfirmOpen(false);
  };
  const handleCancel = () => {
    setIsConfirmOpen(false);
    setPendingUpdate(null);
    setPendingStatusUpdate(null);
    setPendingArchiveId(null);
  };
  return (
    <>
      <Stack
        direction={{ sm: 'column', md: 'row' }}
        justifyContent="space-between"
        position="relative"
      >
        <Stack spacing={1}>
          <Stack direction="row" spacing={5}>
            <Typography variant="h5">{jobPosting.s_title}</Typography>.
            <Typography color="text.secondary">
              {formSalaryString(jobPosting)}
            </Typography>
            {jobPosting?.status === 4 && (
              <Chip
                sx={{ width: 'fit-content' }}
                size="small"
                label="Archived"
                color="dark"
              />
            )}
          </Stack>
          <Typography
            sx={{ marginTop: '0 !important' }}
            color="text.secondary"
            variant="subtitle2"
          >
            {' '}
            {parse(jobPosting.s_description)}
          </Typography>
          <Stack spacing={2} direction="row">
            {jobPosting.status !== 4 ? (
              <>
                <Chip
                  label="Archive"
                  onClick={() => {
                    if (isAppliedChanges) {
                      setPendingArchiveId(jobPosting.id as number);
                      setIsConfirmOpen(true);
                    } else {
                      setArchiveModalId(Number(jobPosting.id));
                    }
                  }}
                  color="dark"
                  variant="outlined"
                  sx={{ cursor: 'pointer' }}
                />
                {jobPostingStatuses.map(({ status, label, color }) => (
                  <Chip
                    key={status}
                    label={label}
                    color={jobPosting.status === status ? color : 'default'}
                    onClick={() => {
                      if (isAppliedChanges) {
                        setIsConfirmOpen(true);
                        setPendingStatusUpdate({
                          id: jobPosting.id as number,
                          status,
                          template_id: templateData.id,
                        });
                      } else {
                        updateJobPosting({
                          id: jobPosting.id as number,
                          status,
                          template_id: templateData.id,
                        });
                      }
                    }}
                  />
                ))}
              </>
            ) : (
              <></>
            )}
          </Stack>
        </Stack>
        {jobPosting.status !== 4 && (
          <Stack spacing={3} ml={2}>
            <Button
              onClick={() => {
                if (isAppliedChanges) {
                  setPendingUpdate(jobPosting);
                  setIsConfirmOpen(true);
                } else {
                  setSelectedJobPosting(jobPosting);
                  setIsJobPostingModalOpen(true);
                }
              }}
              size="small"
              variant="contained"
            >
              Edit
            </Button>
            <Button
              onClick={() => {
                setSelectedJobPosting(jobPosting);
                setIsAboutJobPostingModalOpen(true);
              }}
              size="small"
              variant="outlined"
            >
              View
            </Button>
          </Stack>
        )}
        {isUpdatingJobPosting && (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              backgroundColor: 'rgba(255, 255, 255, 0.6)',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              zIndex: 2,
            }}
          >
            <CircularProgress />
          </Box>
        )}
      </Stack>
      <Divider sx={{ marginTop: '7px' }} />

      <AboutJobPostingModal
        isOpen={isAboutJobPostingModalOpen}
        handleClose={() => {
          setIsAboutJobPostingModalOpen(false);
          setSelectedJobPosting(null);
        }}
        jobPosting={selectedJobPosting}
        templateJob
      />

      {templateData.industry && isJobPostingModalOpen && (
        <TemplateJobPostingModal
          templateId={templateData?.id}
          isOpen={isJobPostingModalOpen}
          handleClose={() => {
            setSelectedJobPosting(null);
            setIsJobPostingModalOpen(false);
          }}
          jobPosting={jobPosting}
          industryId={templateData.industry.id}
        />
      )}

      {archiveJobId && (
        <ArchivePositionModal
          isOpen={Boolean(archiveJobId)}
          handleClose={() => {
            setArchiveModalId(null);
          }}
          jobPostingId={archiveJobId}
          unArchive={false}
          templateId={templateData.id as number}
        />
      )}
      <ConfirmationModal
        open={isConfirmOpen}
        onConfirm={handleConfirm}
        onCancel={handleCancel}
      />
    </>
  );
};

export default TemplatePositionCard;
